import { useState } from 'react';
import { Link, useNavigate } from 'react-router';

import {
  ErrorState,
  Form,
  FormError,
  PageHeader,
  Panel,
  SelectField,
  SubmitButton,
  TextField,
  fieldErrors,
  useNotifications,
} from '../../components';
import { PATHS } from '../../routes/paths';
import { useCreateInsurer, useSnapshotLayouts } from '../queries';
import { ONBOARDING_COPY } from './onboarding';

/**
 * Registering an insurer.
 *
 * The same shape as every other creation screen in the back-office: the form,
 * the server's refusals on the fields they name, and a way out that creates
 * nothing. On success it returns to the list, which refetches -- the new
 * operadora is the one the server reports, not a row this screen invented.
 *
 * The layout is a choice among the layouts the server has registered rather
 * than a code typed by hand, so a code the API would refuse cannot be sent. The
 * default layout needs no list, so it stays available while the list loads and
 * when it fails to.
 */
export function NewInsurerPage() {
  const create = useCreateInsurer();
  const layouts = useSnapshotLayouts();
  const navigate = useNavigate();
  const { notify } = useNotifications();

  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [layoutCode, setLayoutCode] = useState('');

  const errors = fieldErrors(create.error);

  const layoutOptions = [...(layouts.data?.items ?? [])]
    .sort((a, b) => a.code.localeCompare(b.code))
    .map((layout) => ({ value: layout.code, label: `${layout.name} (${layout.code})` }));

  const submit = () => {
    create.mutate(
      {
        code,
        name,
        // The empty option is the default layout, which is what every insurer we
        // send to today uses -- it reaches the API as `null` rather than as an
        // empty layout code it would have to reject.
        layout_code: layoutCode === '' ? null : layoutCode,
      },
      {
        onSuccess: () => {
          notify('success', ONBOARDING_COPY.insurerRegistered);
          void navigate(PATHS.staffInsurers);
        },
      },
    );
  };

  return (
    <div className="page">
      <PageHeader
        title={ONBOARDING_COPY.registerInsurer}
        description={ONBOARDING_COPY.insurersDescription}
        actions={
          <Link className="button button--ghost" to={PATHS.staffInsurers}>
            {ONBOARDING_COPY.backToInsurers}
          </Link>
        }
      />

      <Panel title={ONBOARDING_COPY.registerInsurer}>
        <Form onSubmit={submit} aria-label={ONBOARDING_COPY.registerInsurer}>
          <FormError error={create.error} />
          {layouts.isError && (
            <ErrorState
              error={layouts.error}
              title={ONBOARDING_COPY.layoutsError}
              onRetry={() => void layouts.refetch()}
            />
          )}
          <TextField
            label={ONBOARDING_COPY.insurerCodeLabel}
            value={code}
            onChange={setCode}
            hint={ONBOARDING_COPY.insurerCodeHint}
            required
            {...(errors['code'] !== undefined && { error: errors['code'] })}
          />
          <TextField
            label={ONBOARDING_COPY.insurerNameLabel}
            value={name}
            onChange={setName}
            required
            {...(errors['name'] !== undefined && { error: errors['name'] })}
          />
          <SelectField
            label={ONBOARDING_COPY.insurerLayoutLabel}
            value={layoutCode}
            onChange={setLayoutCode}
            options={layoutOptions}
            placeholder={ONBOARDING_COPY.defaultLayout}
            hint={ONBOARDING_COPY.insurerLayoutHint}
            disabled={layouts.isPending}
            {...(errors['layout_code'] !== undefined && { error: errors['layout_code'] })}
          />
          <div className="form__actions">
            <SubmitButton
              pending={create.isPending}
              pendingLabel={ONBOARDING_COPY.registeringInsurer}
            >
              {ONBOARDING_COPY.registerInsurer}
            </SubmitButton>
            <Link className="button button--ghost" to={PATHS.staffInsurers}>
              {ONBOARDING_COPY.cancel}
            </Link>
          </div>
        </Form>
      </Panel>
    </div>
  );
}
